import { useQuery } from '@tanstack/react-query';
import { api } from './api';

/**
 * Feature flags — "is this build switched on for me?"
 *
 * A build that shipped through Build Mode is exposed behind a flag keyed on the build:
 * `bm-<build-id>`. This is how a gated surface asks whether to render.
 *
 * ONE LIST, NOT N CHECKS. The endpoint returns every flag on for the caller, because a
 * page may gate several surfaces and needs all their answers on first paint — N round
 * trips would each resolve at a different moment and pop controls in one by one. The
 * list is small by construction: a firm's build budget tops out at a handful of concurrent
 * builds.
 *
 * DEFAULT-OFF WHILE LOADING, and that direction matters. A flag hides a feature the
 * firm has not been given yet, so guessing "on" would flash an unavailable surface and,
 * worse, teach the user a control exists before it does. This is the opposite default
 * from the nav gating in navGating.ts, which shows optimistically — there the item
 * already belongs to the user and the only question is which grant reveals it. Here the
 * question is whether the feature exists for them at all.
 */

const FLAGS_QUERY_KEY = ['feature-flags'] as const;

interface FlagsResponse {
  flags: string[];
}

/** Every flag on for the current user. Shared cache — one fetch per page, not per call. */
export function useFlags() {
  return useQuery<FlagsResponse>({
    queryKey: FLAGS_QUERY_KEY,
    queryFn: ({ signal }) => api.get('/api/flags', signal),
    // Flags move when a build ships or is toggled — rare, and never mid-interaction.
    // A minute of staleness costs nothing and keeps this off the hot path.
    staleTime: 60_000,
  });
}

/**
 * Is one named flag on? False while loading and false on error — a feature is never
 * revealed speculatively.
 */
export function useFlag(key: string): boolean {
  const { data } = useFlags();
  return data?.flags.includes(key) ?? false;
}

/** The flag key for a build, so call sites never hand-assemble the `bm-` prefix. */
export function flagKeyForBuild(buildId: string): string {
  return `bm-${buildId}`;
}

/**
 * Product-level flags, namespaced `feat:` away from the per-build `bm-<id>` space.
 *
 * `BUILD_MODE` answers "does this surface exist at all?", which is a different question
 * from "is this build's output on?" — the nav entry and the route both key off it, so a
 * switched-off Build Mode is absent rather than a dead link into a 404.
 */
export const FEATURE = {
  BUILD_MODE: 'feat:build_mode',
} as const;
